import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { formatDate, getActivityColor } from '../../utils/helpers';

const getActivityIcon = (type) => {
  switch (type) {
    case 'Samtale': return '📞';
    case 'E-post': return '📧';
    case 'Møte': return '📅';
    case 'Online møte': return '🎥';
    case 'Oppgave': return '✅';
    case 'Notat': return '📝';
    case 'Dokument': return '📄';
    case 'Opprettet': return '👤';
    case 'Deal': return '🎯';
    default: return '⭐';
  }
};

const ActivityTimeline = ({ activities = [] }) => {
  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-[2.5rem] border border-slate-100">
        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-6">
          <SafeIcon icon="📅" className="w-10 h-10 text-slate-300" />
        </div>
        <h3 className="text-xl font-black text-slate-900 italic">Ingen historikk enda</h3>
        <p className="text-slate-500 font-medium mt-2 max-w-xs mx-auto">
          Start med å logge en samtale eller opprette en oppgave for å bygge tidslinjen.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical Line */}
      <div className="absolute left-[27px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-100 via-slate-100 to-transparent" />
      
      <div className="space-y-10 relative">
        {activities.map((activity, index) => {
          const colors = getActivityColor(activity.type);
          const Icon = getActivityIcon(activity.type);
          const isDone = activity.status === 'Gjennomført';
          
          return (
            <motion.div
              key={activity.activity_id || index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex gap-8 group"
            >
              {/* Icon Container */}
              <div className="relative shrink-0">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center relative z-10 transition-all duration-300 group-hover:scale-110 shadow-sm border ${isDone ? 'bg-white text-emerald-500 border-emerald-100' : `${colors.bg} ${colors.text} ${colors.border}`}`}>
                  <SafeIcon icon={Icon} className="w-6 h-6" />
                </div>
                {/* Glow effect for active/important items */}
                {!isDone && (
                  <div className={`absolute inset-0 rounded-2xl blur-lg opacity-20 -z-0 ${colors.icon}`} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 pt-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      {formatDate(activity.date)}
                    </span>
                    <div className="w-1 h-1 bg-slate-200 rounded-full" />
                    <span className={`text-[10px] font-black uppercase tracking-widest ${isDone ? 'text-emerald-500' : colors.text}`}>
                      {activity.type}
                    </span>
                  </div>
                  {isDone && (
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[8px] font-black uppercase tracking-widest rounded-full border border-emerald-100">
                      Fullført
                    </span>
                  )}
                </div>
                <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:border-blue-100">
                  <h4 className="text-base font-black text-slate-900 tracking-tight italic mb-2">
                    {activity.title}
                  </h4>
                  {activity.description && (
                    <p className="text-sm text-slate-500 leading-relaxed font-medium">
                      {activity.description}
                    </p>
                  )}
                  {/* Metadata / Actions */}
                  <div className="mt-4 flex items-center gap-4">
                    <div className="flex -space-x-2">
                      <div className="w-6 h-6 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center text-[8px] font-bold text-slate-400">
                        JS
                      </div>
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      Ansvarlig: Jonas Selger
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityTimeline;